import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import carDetailsStyles from 'components/Car/Details/CarDetailsMediaStyles.module.css'
import { Tooltip } from '@material-ui/core'
import { Loader } from 'components/Shared/Loader'
import { ReadOnlyTextField } from 'components/Shared/ReadOnlyTextField'
import { getLabelFromArrayByParameter } from 'components/Order/OrderDetails/Utils'
import { ReadOnlyCheckbox } from 'components/Shared/ReadOnlyCheckbox'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import { downloadCarFile, fetchDriver, getCarFile } from 'services'
import { fetchCar, fetchCarLocationServicesConnectionStatus } from 'services/CarService'
import errorIcon from 'assets/fields/error.svg'
import optionalFeatures from 'components/Car/carOptionalFeatures'
import Sections from 'components/Car/sections'
import { useErrorHandling } from 'context/errorHandling'
import { HTTP_404_ERROR } from 'Constants'
import { DetailsColumn, DetailsRow } from 'components/Shared/CrudComponents'
import { fetchContractor } from 'services/ContractorService'
import Module from 'constants/customFieldModules'
import { CustomFieldsProvider } from 'context/customFields'
import { FilesPreviewDialog } from 'components/Shared/FilesPreviewDialog'
import { FileChip } from 'components/Shared/FileChip'
import {
  CAR_ATTACHMENTS_READ,
  CAR_CONTRACTOR_READ,
  CAR_DATA_READ,
  CAR_DRIVER_READ,
  CAR_EDIT,
  useAuth
} from 'context/auth'

export function CarDetails (props) {
  const [car, setCar] = useState(null)
  const [carrier, setCarrier] = useState(null)
  const [driver, setDriver] = useState(null)
  const [locationServicesConnectionStatus, setLocationServicesConnectionStatus] = useState(null)
  const [previewedFile, setPreviewedFile] = useState(null)
  const { hasAnyPrivileges } = useAuth()

  const carId = props.match.params.id
  const fileId = props.match.params.fileId

  const isDisconnectedWithLocationServices = locationServicesConnectionStatus && !locationServicesConnectionStatus.connected

  const { handleError } = useErrorHandling()
  const history = useHistory()

  const handleNotFoundError = useCallback((error, message) =>
    handleError(error, { [HTTP_404_ERROR]: message }
    ).then(() => history.push('/cars')), [handleError, history])

  useEffect(() => {
      fetchCar(carId)
        .then(setCar)
        .catch((error) => handleNotFoundError(error, 'notifications.error.getCar'))

      fetchCarLocationServicesConnectionStatus(carId)
        .then(resp => {
          setLocationServicesConnectionStatus(resp.status)
        })
        .catch(error => {
          console.error('Could not fetch location services connection status', error)
        })
    }, [carId, handleNotFoundError],
  )

  const hasDriverReadPrivileges = hasAnyPrivileges([CAR_DRIVER_READ])
  useEffect(() => {
    if (car?.driverId && hasDriverReadPrivileges) {
      fetchDriver(car.driverId)
        .then(response =>
          setDriver(response))
        .catch((error) => handleNotFoundError(error, 'notifications.error.getDriver'))
    }
  }, [car, hasDriverReadPrivileges, handleNotFoundError])

  const hasContractorReadPrivilege = hasAnyPrivileges([CAR_CONTRACTOR_READ])
  useEffect(() => {
    if (car?.carrierId && hasContractorReadPrivilege) {
      fetchContractor(car.carrierId)
        .then(response => setCarrier(response))
        .catch((error) => handleNotFoundError(error, 'notifications.error.getCarrier'))
    }
  }, [car, hasContractorReadPrivilege, handleNotFoundError])

  const { t } = useTranslation()

  const editCar = () => {
    history.push('/cars/edit/' + carId)
  }

  const setPreviewFile = (file) => history.push('/cars/details/' + carId + '/files/' + file.id)

  useEffect(() => {
    const previewedAttachment = fileId ? car?.files?.find(file => file.id === fileId) : null
    setPreviewedFile(previewedAttachment)
  }, [fileId, car])

  const hasCarDataReadPrivilege = hasAnyPrivileges([CAR_DATA_READ])
  const title = hasCarDataReadPrivilege && car ? car.registrationNumber : t('car.details.title')
  return <div>
    {car ?
      <>
        <TitleBar
          buttonLabel={t('car.details.edit')}
          title={title}
          onButtonClick={editCar}
          renderTitle={title => (
            <div className={carDetailsStyles.title}>
              <h1>{title}</h1>
              {isDisconnectedWithLocationServices &&
                <Tooltip title={t('car.details.location-services-connection-warning')} arrow>
                  <img
                    className={carDetailsStyles.warningIcon}
                    src={errorIcon}
                    alt="Location services warning"/>
                </Tooltip>
              }
            </div>
          )
          }
          isButtonDisabled={!hasAnyPrivileges([CAR_EDIT])}
        />
        <CustomFieldsProvider module={Module.Cars} entityId={carId}>
          <DetailsRow>
            <DetailsColumn section={Sections.Carrier} visible={hasContractorReadPrivilege}>
              <ReadOnlyTextField clearDisabled label={'car.details.carrier'}
                                 value={carrier ? t(carrier.fullName) : ''}/>
            </DetailsColumn>
            <DetailsColumn section={Sections.Car} visible={hasCarDataReadPrivilege}>
              <ReadOnlyTextField clearDisabled label={'car.details.registrationNumber'}
                                 value={car.registrationNumber}/>
              <ReadOnlyTextField clearDisabled label={'car.details.brand'} value={car.brand}/>
              <ReadOnlyTextField clearDisabled label={'car.details.model'} value={car.model}/>
              <ReadOnlyTextField clearDisabled label={'car.details.trailerRegistrationNumber'}
                                 value={car.trailerRegistrationNumber}/>
              <ReadOnlyTextField clearDisabled label={'car.details.type'} value={car.type}/>
              <ReadOnlyTextField clearDisabled label={'car.details.capacity'} value={car.capacity}/>
              <ReadOnlyTextField clearDisabled label={'car.details.loadingSpace'} value={car.loadingSpace}/>
              <div className={carDetailsStyles.col}>
                {t('car.details.optionalFeatures.title')}
                <div className={carDetailsStyles.container}>
                  {car.optionalFeatures.map(feature => <ReadOnlyCheckbox
                    key={feature}
                    label={
                      'car.details.optionalFeatures.' +
                      t(getLabelFromArrayByParameter(feature, optionalFeatures, 'id'))}/>)}
                </div>
              </div>
            </DetailsColumn>
            <DetailsColumn section={Sections.Driver} visible={hasDriverReadPrivileges}>
              <ReadOnlyTextField clearDisabled label={'car.details.driver.firstName'}
                                 value={driver ? driver.firstName : '-'}/>
              <ReadOnlyTextField clearDisabled label={'car.details.driver.lastName'}
                                 value={driver ? driver.lastName : '-'}/>
              <ReadOnlyTextField clearDisabled label={'car.details.driver.phoneNumber'}
                                 value={driver ? driver.phoneNumber : '-'}/>
            </DetailsColumn>
            <DetailsColumn section={Sections.Files} visible={hasAnyPrivileges([CAR_ATTACHMENTS_READ])}>
              <div className={carDetailsStyles.files}>
                <FilesPreviewDialog open={!!previewedFile}
                                    fileToShow={previewedFile}
                                    filesSource={getCarFile}
                                    files={car.files}
                                    onClose={() => {
                                      history.push('/cars/details/' + car.id)
                                    }}
                                    onFileChanged={setPreviewFile}
                />
                <ul className="d-flex flex-column p-0">
                  {car.files.map(file => <FileChip
                    key={file.id}
                    file={file}
                    maxTextLength={30}
                    onDownload={() => downloadCarFile(file)}
                    onShow={() => setPreviewFile(file)}
                  />)}
                </ul>
              </div>
            </DetailsColumn>
          </DetailsRow>
        </CustomFieldsProvider>
      </>
      :
      <Loader/>
    }
  </div>
}
